<template>
  <v-container fluid class="analysis-run-detail">
    <v-skeleton-loader
      v-if="loading && !run"
      type="heading, chip@4, list-item-three-line, table-row@5"
      class="mt-2"
    />

    <v-alert v-else-if="error && !run" type="error" outlined class="mt-2">
      <div class="d-flex align-center">
        <span>{{ error }}</span>
        <v-spacer />
        <v-btn small outlined color="error" @click="load">
          <v-icon small left>mdi-refresh</v-icon>
          Retry
        </v-btn>
      </div>
    </v-alert>

    <template v-else-if="run">
      <!-- Header: everything needed to say what this measurement was -->
      <v-card elevation="2" class="analysis-run-detail__header">
        <div class="analysis-run-detail__title-row">
          <div class="analysis-run-detail__title">
            <v-icon left color="primary">mdi-play-box-outline</v-icon>
            <span class="headline">{{ title }}</span>
            <v-chip x-small label :color="status.color" dark class="ml-3">
              <v-icon x-small left>{{ status.icon }}</v-icon>
              {{ status.label }}
            </v-chip>
            <FolderRunFreshnessChip :freshness="run.freshness" small class="ml-2" />
          </div>
          <v-spacer />
          <v-btn
            small
            outlined
            class="mr-2 analysis-run-detail__compare"
            @click="compareDialog = true"
          >
            <v-icon small left>mdi-compare-horizontal</v-icon>
            Compare
          </v-btn>
          <v-btn
            small
            color="primary"
            depressed
            class="analysis-run-detail__rerun"
            :loading="rerunning"
            @click="rerun"
          >
            <v-icon small left>mdi-refresh</v-icon>
            Re-run with current version
          </v-btn>
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn icon small class="ml-1" v-bind="attrs" v-on="on">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item to="/engine-changelog">
                <v-list-item-title>View engine changelog</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="run.rerun_of != null" :to="`/runs/${run.rerun_of}`">
                <v-list-item-title>Open the original run</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="deleteDialog = { open: true, busy: false, error: '' }">
                <v-list-item-title class="error--text">Delete run</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <div class="caption grey--text text--darken-1 analysis-run-detail__meta">
          {{ metaLine }}
        </div>

        <div class="analysis-run-detail__chips">
          <v-chip x-small outlined label>{{ configLabel }}</v-chip>
          <v-chip v-for="engine in run.engines || []" :key="`req-${engine}`" x-small outlined label>
            {{ engine }}
          </v-chip>
        </div>
        <div class="analysis-run-detail__chips">
          <v-chip
            v-for="chip in engineChips"
            :key="chip.key"
            x-small
            outlined
            label
            :color="chip.color || undefined"
            :title="chip.tooltip"
          >
            {{ chip.text }}
          </v-chip>
          <v-chip
            v-if="run.build_sha"
            x-small
            outlined
            label
            :title="`Worker build ${run.build_sha}`"
          >
            build {{ shortSha }}
          </v-chip>
          <span v-if="!engineChips.length" class="caption grey--text">
            Engine versions not recorded for this run
          </span>
        </div>

        <div class="analysis-run-detail__freshness caption" :class="`${freshness.color}--text`">
          <v-icon x-small left :color="freshness.color">{{ freshness.icon }}</v-icon>
          {{ freshness.detail }}
        </div>

        <!-- Live progress -->
        <div v-if="active" class="analysis-run-detail__progress">
          <v-progress-linear
            :value="progressPercent"
            height="6"
            rounded
            color="primary"
            class="analysis-run-detail__progress-bar"
          />
          <span class="caption font-weight-medium">{{ progressCaption }}</span>
          <span class="caption grey--text ml-2">{{ runTime }} elapsed</span>
        </div>

        <p v-if="run.note" class="body-2 grey--text text--darken-2 mb-0 mt-2">{{ run.note }}</p>
      </v-card>

      <!-- Metrics -->
      <v-card outlined class="analysis-run-detail__metrics">
        <div class="analysis-run-detail__section">Coherence evaluation</div>
        <FolderRunMetrics
          :metrics="run.metrics"
          :delta="run.delta"
          :against-label="againstLabel"
          :show-delta-slot="false"
        />
      </v-card>

      <v-alert v-if="error" dense text type="warning" class="analysis-run-detail__refresh-error">
        {{ error }}
      </v-alert>

      <!-- Videos -->
      <v-card outlined class="analysis-run-detail__videos">
        <div class="analysis-run-detail__section">
          Videos ({{ videos.length }})
          <span class="analysis-run-detail__outcome-legend caption grey--text">
            Detected / Missed / False alarm / Cleared
          </span>
        </div>

        <div v-if="!videos.length" class="analysis-run-detail__empty caption grey--text">
          This run has no videos. The folder was empty when it was launched.
        </div>

        <v-data-table
          v-else
          :headers="headers"
          :items="videoRows"
          item-key="key"
          :items-per-page="25"
          :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
          class="analysis-run-detail__table"
        >
          <template #[`item.video`]="{ item }">
            <div class="font-weight-medium">{{ item.name }}</div>
            <div class="caption grey--text">
              {{ item.runIdShort }}
            </div>
          </template>
          <template #[`item.analysis`]="{ item }">
            <v-chip x-small label :color="item.status.color" dark>{{ item.status.label }}</v-chip>
            <v-chip v-if="item.degraded" x-small label color="error" dark class="ml-1">
              degraded
            </v-chip>
            <v-chip v-if="item.partial" x-small label color="warning" dark class="ml-1">
              partial
            </v-chip>
            <div v-if="item.stepCaption" class="caption grey--text">{{ item.stepCaption }}</div>
          </template>
          <template #[`item.result`]="{ item }">
            <span v-if="item.result">{{ item.result }}</span>
            <span v-else class="grey--text">—</span>
          </template>
          <template #[`item.label`]="{ item }">
            <v-chip
              v-if="!item.label.empty"
              x-small
              outlined
              label
              :color="item.label.color"
              class="analysis-run-detail__label-chip"
            >
              {{ item.label.label }}
            </v-chip>
            <span v-else class="grey--text caption">Unlabelled</span>
          </template>
          <template #[`item.outcome`]="{ item }">
            <v-chip
              v-if="!item.outcome.empty"
              x-small
              label
              :color="item.outcome.color"
              dark
              :title="item.outcome.tooltip"
            >
              <v-icon x-small left>{{ item.outcome.icon }}</v-icon>
              {{ item.outcome.label }}
            </v-chip>
            <span v-else class="grey--text" :title="item.outcome.tooltip">—</span>
          </template>
          <template #[`item.duration`]="{ item }">{{ item.duration }}</template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              v-if="item.link"
              icon
              small
              title="Open the analysis of this video for this run"
              :to="item.link"
            >
              <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <FolderRunCompareDialog
        v-model="compareDialog"
        :runs="compareCandidates"
        :initial-a="run.delta ? run.delta.against_folder_run_id : null"
        :initial-b="run.id"
      />

      <v-dialog v-model="deleteDialog.open" max-width="440">
        <v-card>
          <v-card-title class="px-6">
            <h5 class="primary-color mb-0">Delete run</h5>
          </v-card-title>
          <v-card-text class="px-6">
            Delete <strong>{{ title }}</strong
            >? Only the run record is removed — the analyses of its videos and their results are
            kept.
            <v-alert v-if="deleteDialog.error" dense text type="error" class="mt-3 mb-0">
              {{ deleteDialog.error }}
            </v-alert>
          </v-card-text>
          <v-divider />
          <v-card-actions class="px-6 py-3">
            <v-spacer />
            <v-btn
              text
              class="cancel-modal"
              :disabled="deleteDialog.busy"
              @click="deleteDialog.open = false"
            >
              Cancel
            </v-btn>
            <v-btn color="error" depressed :loading="deleteDialog.busy" @click="remove">
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <v-snackbar v-model="snackbar.visible" :timeout="6000" :color="snackbar.color" bottom>
      {{ snackbar.text }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.visible = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import FolderRunCompareDialog from '@/components/folderRuns/FolderRunCompareDialog.vue';
import FolderRunFreshnessChip from '@/components/folderRuns/FolderRunFreshnessChip.vue';
import FolderRunMetrics from '@/components/folderRuns/FolderRunMetrics.vue';
import FolderRunsService from '@/services/FolderRunsService';
import { POLL_INTERVAL_MS } from '@/store/modules/analysisQueue';
import {
  configCaption,
  describeLaunchError,
  engineVersionChips,
  folderCaption,
  folderRunStatusPresentation,
  formatDateTime,
  formatDurationMs,
  formatRunTime,
  freshnessChip,
  isFolderRunActive,
  labelPresentation,
  outcomePresentation,
  progressText,
  progressValue,
  shortHash,
  toNumber,
} from '@/utils/folderRuns';
import { runStatusPresentation } from '@/utils/runLabels';

/**
 * /runs/:id — one Folder Run in full.
 *
 * The header is the provenance record: folder, configuration version, requested engines, the
 * engine versions actually executed, the build and the freshness verdict. `Re-run with current
 * version` repeats the SAME frozen member snapshot and configuration with today's engines, which
 * is the only way to get a one-variable comparison.
 */
export default {
  name: 'AnalysisRunDetail',

  components: { FolderRunCompareDialog, FolderRunFreshnessChip, FolderRunMetrics },

  data() {
    return {
      run: null,
      videos: [],
      siblings: [],
      loading: false,
      error: '',
      rerunning: false,
      compareDialog: false,
      deleteDialog: { open: false, busy: false, error: '' },
      snackbar: { visible: false, text: '', color: 'success' },
      pollTimer: null,
      visibilityHandler: null,
    };
  },

  computed: {
    runId() {
      return this.$route.params.id;
    },
    title() {
      return this.run?.name || folderCaption(this.run);
    },
    status() {
      return folderRunStatusPresentation(this.run?.status);
    },
    freshness() {
      return freshnessChip(this.run?.freshness);
    },
    active() {
      return isFolderRunActive(this.run);
    },
    configLabel() {
      return configCaption(this.run?.config);
    },
    engineChips() {
      return engineVersionChips(this.run?.engine_versions, {
        outdatedEngines: this.run?.freshness?.outdated_engines,
      });
    },
    shortSha() {
      return shortHash(this.run?.build_sha, 7);
    },
    progressPercent() {
      return progressValue(this.run);
    },
    progressCaption() {
      return progressText(this.run);
    },
    runTime() {
      return formatRunTime(this.run);
    },
    againstLabel() {
      const againstId = this.run?.delta?.against_folder_run_id;
      return againstId == null ? '' : `vs run #${againstId}`;
    },
    /** The compare selector needs more than this run; siblings of the same folder are enough. */
    compareCandidates() {
      const list = Array.isArray(this.siblings) ? [...this.siblings] : [];
      if (this.run && !list.some((item) => Number(item.id) === Number(this.run.id))) {
        list.unshift(this.run);
      }
      return list;
    },
    metaLine() {
      const run = this.run;
      if (!run) return '';
      const parts = [folderCaption(run)];
      parts.push(`created ${formatDateTime(run.created_at)}`);
      if (run.started_at) parts.push(`started ${formatDateTime(run.started_at)}`);
      if (run.finished_at) parts.push(`finished ${formatDateTime(run.finished_at)}`);
      const duration = formatDurationMs(run.duration_ms);
      if (duration !== '—') parts.push(`${duration} total`);
      if (run.rerun_of != null) parts.push(`re-run of #${run.rerun_of}`);
      return parts.filter(Boolean).join(' · ');
    },
    headers() {
      return [
        { text: 'Video', value: 'video' },
        { text: 'Analysis', value: 'analysis', sortable: false },
        { text: 'Result', value: 'result', sortable: false },
        { text: 'Label', value: 'label', sortable: false, width: 110 },
        { text: 'Outcome', value: 'outcome', sortable: false, width: 140 },
        { text: 'Duration', value: 'duration', align: 'end', width: 110 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 60 },
      ];
    },
    videoRows() {
      return this.videos.map((video, index) => {
        const runId = video.run_id ? String(video.run_id) : null;
        const sourceId = video.source_id;
        return {
          key: `${sourceId ?? 'x'}-${index}`,
          name: video.video_name || `Video ${sourceId ?? index + 1}`,
          runIdShort: runId ? `run ${shortHash(runId, 8)}` : 'not queued',
          status: runStatusPresentation(video.status),
          degraded: Boolean(video.degraded),
          partial: Boolean(video.partial),
          stepCaption: this.stepCaption(video),
          result: this.resultCaption(video.result),
          label: labelPresentation(video.label),
          outcome: outcomePresentation(video.outcome),
          duration: formatDurationMs(video.duration_ms),
          // Deep link to the per-video analysis pinned to THIS run.
          link:
            sourceId == null
              ? null
              : `/coherence-analysis/${encodeURIComponent(sourceId)}${
                  runId ? `?run=${encodeURIComponent(runId)}` : ''
                }`,
        };
      });
    },
  },

  watch: {
    '$route.params.id'() {
      this.load();
    },
  },

  mounted() {
    this.load();
    this.startPolling();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    async load({ silent = false } = {}) {
      if (!silent) this.loading = true;
      try {
        const response = await FolderRunsService.get(this.runId);
        const body = response?.data?.data ?? response?.data ?? null;
        if (!body || body.id == null) throw new Error('empty');
        this.run = body;
        this.videos = Array.isArray(body.videos) ? body.videos : [];
        this.error = '';
        this.loadSiblings();
      } catch (error) {
        this.error = silent
          ? 'The run could not be refreshed. The values below may be stale.'
          : 'Failed to load this run.';
        if (!silent) this.run = null;
      } finally {
        this.loading = false;
      }
    },

    /** Runs of the same folder, so Compare has something to offer without leaving the page. */
    async loadSiblings() {
      if (this.siblings.length) return;
      try {
        const response = await FolderRunsService.list({
          limit: 50,
          folder_id: this.run?.folder?.id ?? undefined,
        });
        const body = response?.data?.data ?? response?.data ?? {};
        this.siblings = Array.isArray(body.items) ? body.items : [];
      } catch (error) {
        this.siblings = [];
      }
    },

    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(() => {
        if (this.active) this.load({ silent: true });
      }, POLL_INTERVAL_MS);
      this.visibilityHandler = () => {
        if (typeof document === 'undefined') return;
        if (document.visibilityState === 'hidden') {
          this.clearTimer();
        } else if (!this.pollTimer) {
          this.load({ silent: true });
          this.startPolling();
        }
      };
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', this.visibilityHandler);
      }
    },

    clearTimer() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    stopPolling() {
      this.clearTimer();
      if (this.visibilityHandler && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }
      this.visibilityHandler = null;
    },

    stepCaption(video) {
      const pct = toNumber(video.progress_pct);
      const step = video.current_step ? String(video.current_step) : '';
      if (!step && pct === null) return '';
      return [step, pct === null ? '' : `${pct}%`].filter(Boolean).join(' · ');
    },

    /** 'match 62 · 3 alerts' — whatever part of the result doc made it through. */
    resultCaption(result) {
      if (!result || typeof result !== 'object') return '';
      const parts = [];
      const match = toNumber(result.global_match);
      if (match !== null) parts.push(`match ${match}`);
      const alerts = toNumber(result.alerts);
      if (alerts !== null) parts.push(`${alerts} ${alerts === 1 ? 'alert' : 'alerts'}`);
      return parts.join(' · ');
    },

    notify(text, color = 'success') {
      this.snackbar = { visible: true, text, color };
    },

    async rerun() {
      this.rerunning = true;
      try {
        const response = await FolderRunsService.rerun(this.runId, { socketId: this.$socketId });
        const body = response?.data?.data ?? response?.data ?? {};
        const accepted = Array.isArray(body.accepted) ? body.accepted.length : 0;
        this.notify(`Re-run queued · ${accepted} ${accepted === 1 ? 'video' : 'videos'}`);
        if (body.id != null) this.$router.push(`/runs/${body.id}`);
        else this.load();
      } catch (error) {
        this.notify(describeLaunchError(error), 'error');
      } finally {
        this.rerunning = false;
      }
    },

    async remove() {
      this.deleteDialog.busy = true;
      this.deleteDialog.error = '';
      try {
        await FolderRunsService.remove(this.runId);
        this.deleteDialog.open = false;
        this.$router.push('/runs');
      } catch (error) {
        this.deleteDialog.error =
          error?.response?.data?.message || 'Could not delete the run. Try again.';
      } finally {
        this.deleteDialog.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.analysis-run-detail {
  padding-top: 72px;
  max-width: 1400px;
}
.analysis-run-detail__header {
  padding: 16px;
  border-radius: 10px !important;
  margin-bottom: 12px;
}
.analysis-run-detail__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 8px;
}
.analysis-run-detail__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
}
.analysis-run-detail__meta {
  margin-top: 6px;
}
.analysis-run-detail__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.analysis-run-detail__freshness {
  margin-top: 8px;
  display: flex;
  align-items: center;
}
.analysis-run-detail__progress {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.analysis-run-detail__progress-bar {
  flex: 1 1 200px;
  max-width: 280px;
}
.analysis-run-detail__metrics,
.analysis-run-detail__videos {
  border-radius: 10px !important;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.analysis-run-detail__section {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #757575;
  margin-bottom: 10px;
}
.analysis-run-detail__outcome-legend {
  text-transform: none;
  letter-spacing: normal;
  margin-left: 8px;
  font-weight: 400;
}
.analysis-run-detail__refresh-error {
  margin-bottom: 12px;
}
.analysis-run-detail__empty {
  padding: 24px 0;
  text-align: center;
}
</style>
